import { useCallback, useEffect, useState } from "react";

export const USERS_ENDPOINT = "https://dummyjson.com/users";

/**
 * Fetches users from the API endpoint.
 *
 * @returns {Promise<Array>} A promise that resolves to an array of user objects
 * @throws {Error} Throws an error if the request fails
 *
 * - Fetch data from USERS_ENDPOINT
 * - If the request is not ok, throw an error with message: `Request failed with status <status>`
 * - If the request is ok, return the array of users from the response
 */
export const fetchUsers = async () => {
  // Make asynchronous HTTP request
  const response = await fetch(USERS_ENDPOINT);

  // Check if status code isn't 200-299
  if (!response.ok) {
    // If it fails then throw an error with that status code
    throw new Error(`Request failed with status ${response.status}`);
  }

  // Otherwise we parse the JSON response and extract the array from API
  const data = await response.json();
  return data.users;
};

/**
 * Custom hook to fetch and manage users data from the API.
 *
 * @returns {Object} An object containing:
 *   @property {Array} users - Array of user objects from the API
 *   @property {boolean} loading - True while data is being fetched, false otherwise
 *   @property {Error|null} error - Error object if the request failed, null otherwise
 *   @property {Function} refresh - Function to manually re-fetch the users data
 *
 * - Manage three state variables: users, loading, and error
 * - Automatically fetch users when the component mounts
 * - Provide a refresh function to re-fetch data
 * - Handle loading and error states appropriately
 */
export const useUsersData = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refreshes users from the API
  const refresh = useCallback(async () => {
    setLoading(true); // update Loading state to tell UI we're fetching data
    setError(null); // clears any previous errors

    try {
      // Attempt to fetch users and save them
      const fetchedUsers = await fetchUsers();
      setUsers(fetchedUsers);
    } catch (err) {
      // If it fails
      setError(err); //Save the error
      setUsers([]); // Clear users array
    } finally {
      // Always turn off loading when done (no matter success/fail)
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { users, loading, error, refresh };
};
